import React, { useState } from "react";

// Simplified recursive JSON viewer with clean styling

interface JsonTreeViewerProps {
  data: any;
  name?: string; // Key name
  isLast?: boolean; // Whether it is the last item in the parent
  level?: number;
  initialExpanded?: boolean;
}

const theme = {
  key: "#22863a", // green
  string: "#032f62", // dark blue
  number: "#005cc5", // blue
  boolean: "#d73a49", // red
  null: "#586069", // gray
  bracket: "#24292e", // black
  background: "transparent",
  font: 'Menlo, Monaco, "Courier New", monospace',
  fontSize: "13px",
  lineHeight: "1.5",
  arrow: "#6a737d",
};

const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({
  data,
  name,
  isLast = true,
  level = 0,
  initialExpanded = false,
}) => {
  // Auto collapse deep levels
  const [collapsed, setCollapsed] = useState(level > 1 && !initialExpanded);
  const [hovered, setHovered] = useState(false);

  const isArray = Array.isArray(data);
  const isObject = data !== null && typeof data === "object";
  const isEmpty = isObject && (isArray ? data.length === 0 : Object.keys(data).length === 0);

  // Calculate display for collapsed state
  const itemCount = isObject ? (isArray ? data.length : Object.keys(data).length) : 0;
  const collapsedPreview = isArray ? `Array(${itemCount})` : `Object {${itemCount}}`;

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed(!collapsed);
  };

  const copyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  const renderWrapper = (children: React.ReactNode) => (
    <div
      style={{
        fontFamily: theme.font,
        fontSize: theme.fontSize,
        lineHeight: theme.lineHeight,
        paddingLeft: level > 0 ? "20px" : "5px", // Add slight padding to root
        position: "relative",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </div>
  );

  const renderKey = () =>
    name ? <span style={{ color: theme.key, fontWeight: 600 }}>{name}: </span> : null;

  if (!isObject) {
    let valueColor = theme.string;
    let displayValue: string = String(data);

    if (typeof data === "number") valueColor = theme.number;
    else if (typeof data === "boolean") valueColor = theme.boolean;
    else if (data === null) {
      valueColor = theme.null;
      displayValue = "null";
    } else if (typeof data === "string") {
      displayValue = `"${data}"`;
    }

    return renderWrapper(
      <div style={{ display: "flex", alignItems: "center" }}>
        {renderKey()}
        <span style={{ color: valueColor, marginLeft: name ? "5px" : "0" }}>{displayValue}</span>
        {!isLast && <span style={{ color: theme.bracket }}>,</span>}
      </div>,
    );
  }

  // Object or Array
  return renderWrapper(
    <div>
      <div
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        onClick={toggleCollapse}
      >
        {/* Arrow */}
        {!isEmpty && (
          <span
            style={{
              display: "inline-block",
              width: "12px",
              marginRight: "4px",
              marginLeft: "-16px", // Pull out to gutter
              color: theme.arrow,
              transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
              fontSize: "10px",
            }}
          >
            ▼
          </span>
        )}

        {renderKey()}

        {/* Brackets or Content */}
        <span style={{ color: theme.bracket, marginLeft: name ? "5px" : "0" }}>
          {isArray ? "[" : "{"}
        </span>

        {/* Collapsed Preview */}
        {collapsed && !isEmpty && (
          <span style={{ color: "#6a737d", fontStyle: "italic", margin: "0 5px" }}>
            {collapsedPreview} ...
          </span>
        )}

        {/* Closing Bracket if collapsed or empty */}
        {(collapsed || isEmpty) && (
          <span style={{ color: theme.bracket }}>
            {isArray ? "]" : "}"}
            {!isLast && ","}
          </span>
        )}

        {/* Quick Copy Button (visible on hover) */}
        {hovered && (
          <button
            onClick={copyToClipboard}
            title="Copy JSON"
            style={{
              marginLeft: "10px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "12px",
              color: "#0366d6",
              opacity: 0.7,
            }}
          >
            📋
          </button>
        )}
      </div>

      {/* Children */}
      {!collapsed && !isEmpty && (
        <div>
          {isArray
            ? data.map((item: any, index: number) => (
                <JsonTreeViewer
                  key={index}
                  // Arrays normally don't show keys, but we can pass index if needed.
                  // Professional tools usually just list items.
                  data={item}
                  isLast={index === data.length - 1}
                  level={level + 1}
                />
              ))
            : Object.keys(data)
                .sort((a, b) => a.localeCompare(b))
                .map((key, index, arr) => (
                <JsonTreeViewer
                  key={key}
                  name={key}
                  data={data[key]}
                  isLast={index === arr.length - 1}
                  level={level + 1}
                />
              ))}
          <div style={{ color: theme.bracket }}>
            {isArray ? "]" : "}"}
            {!isLast && ","}
          </div>
        </div>
      )}
    </div>,
  );
};

export default JsonTreeViewer;
